import { ComputedRef, PropType, Ref } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { MetadataField } from "@scrile/api-provider/dist/api/FileProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { UserPublicData as OrigUserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { $t } from "@/lib/i18nHelpers";
import { AttachmentFile, PostFileData } from "@/types";
import { AddingFilesOptions } from "@/components/PostCreate/types";

export enum EVENTS {
  STATUS_SHOW = "STATUS_SHOW",
  STATUS_HIDE = "STATUS_HIDE",

  NOTIFICATION_SHOW = "NOTIFICATION_SHOW",
  NOTIFICATION_HIDE = "NOTIFICATION_HIDE",
  NOTIFICATIONS_MARKED_READ = "NOTIFICATIONS_MARKED_READ",
  NOTIFICATIONS_NEW_NOTIFICATION = "NOTIFICATIONS_NEW_NOTIFICATION",

  SHOW_CREATE_POST = "SHOW_CREATE_POST",
  CONTENT_FEED_UPDATED = "CONTENT_FEED_UPDATED",
  CONTENT_FEED_PURCHASE = "CONTENT_FEED_PURCHASE",
  CONTENT_FEED_PURCHASED = "CONTENT_FEED_PURCHASED",
  CONTENT_FEED_PRICE_LOADED = "CONTENT_FEED_PRICE_LOADED",
  CONTENT_FEED_PRICE_CHANGED = "CONTENT_FEED_PRICE_CHANGED",

  SUBSCRIPTION_OFFER_SHOW = "SUBSCRIPTION_OFFER_SHOW",
  MESSAGES_MARK_READ = "MESSAGES_MARK_READ",
  MESSAGES_MARKED_READ = "MESSAGES_MARKED_READ",
  MESSAGES_NEW_MESSAGE = "MESSAGES_NEW_MESSAGE",
  MESSAGES_UPDATE_MESSAGE = "MESSAGES_UPDATE_MESSAGE",
  MESSAGES_UPDATE_THREAD = "MESSAGES_UPDATE_THREAD",
  MESSAGES_LIKED_MESSAGE = "MESSAGES_LIKED_MESSAGE",

  MAIN_MENU_HIDE = "MAIN_MENU_HIDE",
  CALL_OUTGOING = "CALL_OUTGOING",
  EMOJI_PICKER_SHOW = "EMOJI_PICKER_SHOW",

  BALANCE_REFILL_SHOW = "BALANCE_REFILL_SHOW",
  BALANCE_REFILL_PURCHASED = "BALANCE_REFILL_PURCHASED",

  PAYMENT_START_PURCHASE = "PAYMENT_START_PURCHASE",
  PAYMENT_PURCHASED = "PAYMENT_PURCHASED",
  PAYMENT_SUBSCRIBED = "PAYMENT_SUBSCRIBED",

  SHOW_LOGIN_SIGNUP_MODAL = "SHOW_LOGIN_SIGNUP_MODAL",
  BLOCKING_STATUS_CHANGED = "BLOCKING_STATUS_CHANGED",

  SUBSCRIPTION_STATUS_CHANGED = "SUBSCRIPTION_STATUS_CHANGED",

  FOLLOWING_STATUS_CHANGE = "FOLLOWING_STATUS_CHANGE",

  UPCOMING_APPOINTMENT_REMINDER = "UPCOMING_APPOINTMENT_REMINDER",

  STREAM_USER_JOIN = "STREAM_USER_JOIN",
  STORIES_SHOW = "STORIES_SHOW",
  ARCHIVED_STORIES_SHOW = "ARCHIVED_STORIES_SHOW",

  USER_LOGGED_IN = "USER_LOGGED_IN",
  CHOOSE_POST_TYPE = "CHOOSE_POST_TYPE",
  USER_LOGOUT = "USER_LOGOUT",
  PREVENT_LOGOUT = "PREVENT_LOGOUT",

  FUNDRAISING_GOAL_UPDATED = "FUNDRAISING_GOAL_UPDATED",

  AUDIO_PLAYER_INIT = "AUDIO_PLAYER_INIT",
  AUDIO_PLAYER_ACTION = "AUDIO_PLAYER_ACTION",
  AUDIO_PLAYER_RESET = "AUDIO_PLAYER_RESET",

  OPEN_REPORT_MODAL = "OPEN_REPORT_MODAL",

  SENSITIVE_CONTENT_GATE_REQUEST = "SENSITIVE_CONTENT_GATE_REQUEST",
}

export const DEFAULT_LOCALE = "en-US";

export enum sciIconModePostfix {
  DARK = "dark",
  LIGHT = "light",
}

export enum ColorTheme {
  DARK = "dark",
  LIGHT = "light",
}

export enum SideBarPosition {
  LEFT = "left",
  RIGHT = "right",
}

export enum MoreMenuType {
  PAGE = "PAGE",
  POP_UP = "POP_UP",
}

export enum TagType {
  SYSTEM = "system",
  INFORMATION = "information",
}

export enum ROLES {
  MEMBER = "member",
  CREATOR = "creator",
  ADMIN = "admin",
  MANAGER = "manager",
  TALENT_AGENT = "talent_agent",
}

export enum TIME_MEASURAND {
  D = "days",
  H = "hours",
  M = "minutes",
  S = "seconds",
  MS = "milliseconds",
}

export enum CONTENT_FEED_SORTS {
  NEWEST = "IS_FOLLOWING;PUBLISH_TIME,DESC;SET_ID",
  OLDEST = "IS_FOLLOWING;PUBLISH_TIME,ASC;SET_ID",
  RANDOM = "IS_FOLLOWING;RANDOM,DESC",
}

export const REVIEW_COUNT_LIMIT = 3;
export const APPOINTMENT_ONLINE_REMIND_MS = 10 * 60 * 1000;
export const APPOINTMENT_OFFLINE_REMIND_MS = 60 * 60 * 1000;
export const CONTENT_FEED_FIELDS_WITH_REVIEWS_MOCK = "fragment... on ContentFeed { reviews { pageableFragment}}";

export const INPUT_PROPS = {
  label: { type: String as PropType<TranslateResult | string>, default: "" },
  hideLabel: { type: Boolean, default: false },
  name: { type: String, default: "" },
  hint: { type: String },
  value: { type: String, default: "" },
  type: { type: String, default: "text" },
  autocomplete: { type: String },
  placeholder: { type: String as PropType<TranslateResult | string> },
  pattern: { type: String },
  inputmode: { type: String },
  maxlength: { type: String },
  appendIcon: { type: String },
  appendIconSize: { type: String, default: "22" },
  appendIconColor: { type: String },
  prependIcon: { type: String },
  prependIconSize: { type: String, default: "22" },
  prependIconColor: { type: String },
  secondAppendIcon: { type: String },
  secondAppendIconSize: { type: String, default: "22" },
  secondAppendIconColor: { type: String },
  secondPrependIcon: { type: String },
  secondPrependIconSize: { type: String, default: "22" },
  secondPrependIconColor: { type: String },
  rules: { type: [Array, Object, String] as PropType<string[] | Record<string, any> | string>, default: () => [] },
  rulesMessages: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  validationMode: {
    type: String,
    validator: (value: string): boolean =>
      ["aggressive", "passive", "lazy", "eager", "aggressiveNotBlur"].includes(value) || value === undefined,
  },
  invalidField: { type: Boolean, default: false },
  hideErrorMessage: { type: Boolean, default: false },
};

export enum connectionStateEnum {
  EMPTY = "",
  CONNECTED = "connected",
  CONNECTING = "connecting",
  DISCONNECTED = "disconnected",
}

export enum ERRORS {
  SPENDING_BALANCE = "{com.scrile.money.validation.SpendingBalanceValidator.message}",
  PAID_CALL_CREATE = "{com.scrile.paid_calls.validation.PaidCallCreateValidator.message}",
  NO_EVENT_TICKET = "{com.scrile.event_posts.security.EventLiveChatSecurityProvider.message}",
  SLOT_IS_NOT_AVAILABLE = "{com.scrile.appointments.validation.AppointmentBookService.slot-not-available.message}",
  AI_QUESTIONARY_NOT_COMPLETED = "{com.scrile.ai.questionary-not-completed.message}",
  PAYOUT_NOT_ENOUGH_FUNDS_ERROR = "{com.scrile.money.validation.CreatePayoutRequestMinimumAmountValidator.message}",
  PAYOUT_FIELD_AMOUNT_ERROR = "{javax.validation.constraints.Min.message}",
  NSFW_USER_SETTING_ENABLE_RESTRICTED = "{com.scrile.age_verification.DefaultAgeVerificationService.unverified.message}",
}

export enum TabType {
  SEPARATOR = "separator",
}

export enum AppointmentLocationItems {
  DUBAI = "Dubai, UAE",
  LONDON = "London, UK",
}

export const paymentMethodNames = {
  balance: $t("Balance"),
  card: $t("Credit card"),
};

export const SUBSCRIPTION_LEVEL_FRAGMENT: FragmentFields<SubscriptionLevel> = [
  "id",
  "userId",
  "pricePackageId",
  { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
  "contentCount",
  "description",
  "title",
];

export enum STORAGE {
  FORCE_HIDE_PWA_BANNER = "FORCE_HIDE_PWA_BANNER",
  HIDE_PWA_BANNER = "HIDE_PWA_BANNER",
  ONBOARDING = "ONBOARDING",
}

export enum PROVIDED_DATA_KEY {
  OPPONENT_USER = "OPPONENT_USER",
  PAID_CALL_PRICE = "PAID_CALL_PRICE",
  REMOVE_CB = "REMOVE_CB",
  SET_UPDATE_CB = "SET_UPDATE_CB",
  LOADING_UPLOAD_URLS = "LOADING_UPLOAD_URLS",
  FILE_LIST = "FILE_LIST",
  POST_ADD_FILES = "POST_ADD_FILES",
  POST_ADD_FILES_FROM_VAULT = "POST_ADD_FILES_FROM_VAULT",
}

export type PROVIDED_DATA = {
  [PROVIDED_DATA_KEY.OPPONENT_USER]: { user: UserPublicData | OrigUserPublicData | null };
  [PROVIDED_DATA_KEY.PAID_CALL_PRICE]: Ref<number>;
  [PROVIDED_DATA_KEY.LOADING_UPLOAD_URLS]: Ref<boolean>;
  [PROVIDED_DATA_KEY.FILE_LIST]: ComputedRef<PostFileData[]>;
  [PROVIDED_DATA_KEY.REMOVE_CB]: (cb: () => Promise<void>) => void;
  [PROVIDED_DATA_KEY.SET_UPDATE_CB]: (cb: () => Promise<void>) => void;
  [PROVIDED_DATA_KEY.POST_ADD_FILES_FROM_VAULT]: (
    files: AttachmentFile[],
    options?: AddingFilesOptions
  ) => Promise<void>;
  [PROVIDED_DATA_KEY.POST_ADD_FILES]: (
    files: FileList | File[],
    {
      unshift,
      publicAccess,
      metadata,
    }: { unshift?: boolean; publicAccess?: boolean; metadata?: Record<MetadataField | string, string> },
    afterFileUploadCB?: (file?: ContentFile) => void
  ) => Promise<void>;
};

export const MAIN_CONTAINER_WIDTH = 600;
export const CONTENT_FEED_GRID_ITEM_WIDTH = 200;
export const FILE_UPLOADING_FINALLY_PROCESSING = 99.99;

export enum PreventLogoutType {
  CALL_CONTROLLER = "CALL_CONTROLLER",
  CREATE_POST = "CREATE_POST",
}

export enum PurchaseType {
  TOP_UP = "TOP_UP",
  ONE_TIME = "ONE_TIME",
  ONE_TIME_BALANCE = "ONE_TIME_BALANCE",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export enum PaymentMethodType {
  BALANCE = "BALANCE",
  CREDIT_CARD = "CREDIT_CARD",
  CRYPTO = "CRYPTO",
}

export const GRID_PAGE_SIZE = 21;
